import Card from "@/components/Card";
import SectionHeader from "@/components/SectionHeader";
import SkeletonRow from "@/components/SkeletonRow";
import Stepsbar from "@/components/Stepsbar";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { CardContent } from "@/components/ui/card";
import useDataPendaftaran from "@/hooks/useDataPendaftaran";
import { useMultistepForm } from "@/hooks/useMultistepForm";
import useScreen from "@/hooks/useScreen";
import useSessionStorage from "@/hooks/useSessionStorage";
import useTimePeriode from "@/hooks/useTimePeriode";
import { useUser } from "@/hooks/useUser";
import api from "@/lib/axios";
import { formatDate } from "@/lib/utils";
import { getDataStep, getListBerkasPenerimaan } from "@/services/getData";
import { BiodataSate, DataSiswa, Step, SysBerkas } from "@/types";
import { Separator } from "@radix-ui/react-dropdown-menu";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Info,
  Loader2,
  Megaphone,
  Save,
} from "lucide-react";
import { useEffect, useState } from "react";
import { flushSync } from "react-dom";
import toast from "react-hot-toast";
import { LuChevronRight } from "react-icons/lu";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import Biodata from "./partial/biodata/Biodata";
import KartuPeserta from "./partial/kartu-peserta/KartuPeserta";
import Prodi from "./partial/prodi/Prodi";
import UnggahBerkas from "./partial/unggah-berkas/UnggahBerkas";

const saveBiodata = async (form: FormData) => {
  const response = await api.post(
    "/siswa/penerimaan/smbjm/biodata/internasional/update",
    form,
    { withCredentials: true },
  );
  return response.data;
};

export default function PenerimaanInternasionalPage() {
  const { user } = useUser();
  const { isMobile } = useScreen();
  const [searchParams] = useSearchParams();
  // const sessionPeriode?.id_sys_periode = searchParams.get("periode");
  // const stepParams = searchParams.get("step");
  const { isValidPendaftaran } = useDataPendaftaran();
  const {
    isPendingPeriode,
    timePassed,
    dataPeriode,
    timePassedActive,
    dataPeriodeActive,
  } = useTimePeriode();

  const navigate = useNavigate();

  const [sessionPeriode, setSessionPeriode] = useSessionStorage(
    "sessionPeriode",
    {
      id_sys_periode: "",
    },
  );

  const { data: dataStep } = useQuery({
    queryKey: ["dataStep", user?.id, sessionPeriode?.id_sys_periode],
    queryFn: () => getDataStep(sessionPeriode?.id_sys_periode || ""),
  });
  const idDataPendaftaran = dataStep?.id_data_pendaftaran;

  const { data: dataBerkas } = useQuery({
    queryKey: ["listDataBerkas", sessionPeriode?.id_sys_periode],
    queryFn: () =>
      getListBerkasPenerimaan(sessionPeriode?.id_sys_periode || ""),
  });

  const [isPendingSteps, setisPendingSteps] = useState(false);
  const [steps, setSteps] = useState<Step[]>([
    {
      number: 1,
      text: "Personal Information",
      link: "#",
      status: "active",
      tabStatus: "active",
    },
    { number: null, text: null, link: null, status: "nonactive" },
    {
      number: 2,
      text: "Study Program",
      link: "#",
      status: "nonactive",
      tabStatus: "nonactive",
    },
    {
      number: null,
      text: null,
      link: null,
      status: "nonactive",
    },
    {
      number: 3,
      text: "Documents",
      link: "#",
      status: "nonactive",
      tabStatus: "nonactive",
    },
    {
      number: null,
      text: null,
      link: null,
      status: "nonactive",
    },
    {
      number: 4,
      text: "Validation & Participant Card",
      link: "#",
      status: "nonactive",
      tabStatus: "nonactive",
    },
  ]);
  const [currentIndexDisable, setCurrentIndexDisable] = useState(1);
  const [listPanduanPenerimaan, setListPanduanPenerimaan] = useState([
    {
      title: "Personal Information",
      contents: [
        {
          title: "Required Documents",
          instructions: ["Foto"],
        },
        {
          title: "Filling Instructions",
          instructions: [
            "Click the field you want to fill in",
            "Fill in the data correctly according to the requested field",
          ],
        },
      ],
    },
    {
      title: "Study Program",
      contents: [
        {
          title: "Filling Instructions",
          instructions: [
            "View the list of available study programs",
            "Select a study program",
            "Choose a study program concentration (if available)",
            "Choose a study location (if available)",
            "Press 'Save'",
          ],
        },
      ],
    },
    {
      title: "Documents",
      contents: [
        {
          title: "Required Documents",
          instructions: [
            "Can be seen in the table. (Documents labeled with (*Required) are mandatory to upload.)",
          ],
        },
        {
          title: "Filling Instructions",
          instructions: [
            "For documents with a format (indicated by a Download Button in the Format column), download it first",
            "Press the 'Upload' button",
            "Select the document file",
            "Fill in the document number if applicable",
            "Press the 'Save Document File' button",
          ],
        },
      ],
    },
    {
      title: "Participant Card",
      contents: [
        {
          title: "Instructions",
          instructions: [
            "Make sure all registration data has been filled in correctly",
            "Press 'Validate Registration' to validate your registration",
            "Download the participant card by pressing the 'Download' button",
          ],
        },
      ],
    },
  ]);

  const [formBiodata, setFormBiodata] = useState<BiodataSate>({
    id_sys_periode: sessionPeriode?.id_sys_periode || "",
    id_data_siswa: 0,
    id_ref_agama: "",
    id_ref_negara: "",
    nama_lengkap: "",
    nama_ibu_kandung: "",
    handphone: "",
    tanggal_lahir: "",
    tempat_lahir: "",
    jenis_kelamin: "L",
    ukuran_baju: "",
    jalan: "",
    jalan_domisili: "",
    kode_pos: "",
    kelurahan: "",
    dusun: "",
    pilihan_lokasi_kuliah: "singaraja",
    asal_sekolah: "",
    asal_jurusan_sekolah_internasional: "",
    asal_pt: "",
    asal_prodi: "",
    is_bekerja: undefined,
    tempat_bekerja: "",
    penghasilan_bekerja: "",
    tahun_ijazah_terakhir: "",
    nilai_IPK: "",
    accrediting_organization_pt: "",
    accrediting_organization_prodi: "",

    kontak_darurat_hubungan: "",
    kontak_darurat_nama: "",
    kontak_darurat_hp: "",

    is_applying_scholarship: "",
  });
  const [file, setFile] = useState<File | null>(null);
  const [parentProofOfIncomeFile, setParentProofOfIncomeFile] =
    useState<File | null>(null);

  const queryClient = useQueryClient();
  const {
    mutate,
    isPending: isPendingSaveBiodata,
    error,
  } = useMutation({
    mutationFn: saveBiodata,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["dataStep"],
      });
      queryClient.invalidateQueries({
        queryKey: ["dataBiodata"],
      });
      toast.success(data?.message || "Success.");
    },
    onError: (
      error: AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>,
    ) => {
      const err = error as AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>;
      const errorMessage =
        err.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    },
  });

  const {
    currentStep,
    firstStep,
    lastStep,
    backStep,
    goToStep,
    currentIndex,
    currentStepNumber,
  } = useMultistepForm([
    <Biodata
      form={formBiodata}
      setForm={setFormBiodata}
      file={file}
      setFile={setFile}
      error={error}
      parentProofOfIncomeFile={parentProofOfIncomeFile}
      setParentProofOfIncomeFile={setParentProofOfIncomeFile}
    />,
    <Prodi idDataPendaftaran={idDataPendaftaran} />,
    <UnggahBerkas idDataPendaftaran={idDataPendaftaran} />,
    <KartuPeserta idDataPendaftaran={idDataPendaftaran} />,
  ]);

  const [showPanduan, setShowPanduan] = useState(false);

  const handleSaveBiodata = async () => {
    const formData = new FormData();

    Object.keys(formBiodata).forEach((key) => {
      const value = formBiodata[key as keyof DataSiswa];
      if (Array.isArray(value)) {
        // Handle array (kebutuhan_khusus)
        value.forEach((item, index) => {
          if (item !== null && item !== undefined) {
            formData.append(`${key}[${index}]`, String(item)); // Konversi ke string menggunakan String()
          }
        });
      } else {
        // Handle nilai non-array
        if (value !== null && value !== undefined) {
          formData.append(key, String(value)); // Konversi ke string menggunakan String()
        }
      }
    });
    if (file) formData.append("foto", file);
    if (parentProofOfIncomeFile)
      formData.append("parent_proof_of_income_path", parentProofOfIncomeFile);

    mutate(formData);
  };

  const handleNextStep = () => {
    switch (currentStepNumber) {
      case 1:
        if (dataStep?.data_diri === 1) {
          goToStep(2);
          searchParams.set("step", "2");
          navigate(`${location.pathname}?${searchParams.toString()}`);
        }
        break;
      case 2:
        if (dataStep?.data_pilihan_prodi === 1) {
          goToStep(3);
          searchParams.set("step", "3");
          navigate(`${location.pathname}?${searchParams.toString()}`);
        }
        break;
      case 3:
        if (dataStep?.data_berkas === 1) {
          goToStep(4);
          searchParams.set("step", "4");
          navigate(`${location.pathname}?${searchParams.toString()}`);
        }
        break;
    }
  };

  useEffect(() => {
    setisPendingSteps(true);
    if (!dataStep) return;

    setSteps((prev) => {
      const updatedSteps = [...prev];

      // Helper function untuk update status connector (null steps)
      const updateConnectorStatus = (index: number) => {
        if (index <= 0 || index >= updatedSteps.length) return;

        const prevStep = updatedSteps[index - 1];
        if (updatedSteps[index].number === null) {
          updatedSteps[index].status =
            prevStep.status === "completed" ? "completed" : "nonactive";
        }
      };

      // Update main steps status
      updatedSteps.forEach((step, index) => {
        if (!step.text) return; // Skip connector steps

        switch (step.text) {
          case "Personal Information":
            if (dataStep?.data_diri === 1) {
              step.status = "completed";
              step.tabStatus = "active";
              if (updatedSteps[index + 2]) {
                updatedSteps[index + 2].status = "active";
                updatedSteps[index + 2].tabStatus = "active";
              }
              goToStep(2);
              setCurrentIndexDisable(2);
            }
            break;

          case "Study Program":
            if (dataStep?.data_pilihan_prodi === 1) {
              step.status = "completed";
              step.tabStatus = "active";
              if (updatedSteps[index + 2]) {
                updatedSteps[index + 2].status = "active";
                updatedSteps[index + 2].tabStatus = "active";
              }
              goToStep(3);
              setCurrentIndexDisable(3);
            }
            break;
          case "Documents":
            if (dataStep?.data_berkas === 1) {
              step.status = "completed";
              step.tabStatus = "active";
              if (updatedSteps[index + 2]) {
                updatedSteps[index + 2].status = "active";
                updatedSteps[index + 2].tabStatus = "active";
              }
              goToStep(4);
              setCurrentIndexDisable(4);
            }
            break;
          case "Validation & Participant Card":
            if (dataStep?.valid_is_pendaftaran === 1) {
              step.status = "completed";
              step.tabStatus = "active";
              if (updatedSteps[index + 2]) {
                updatedSteps[index + 2].status = "active";
                updatedSteps[index + 2].tabStatus = "active";
              }
              // goToStep(4)
            }
            break;
        }
      });

      // Update connector steps
      updatedSteps.forEach((_, index) => {
        updateConnectorStatus(index);
      });

      return updatedSteps;
    });

    setisPendingSteps(false);
  }, [goToStep, dataStep]);

  useEffect(() => {
    setSteps((prev) =>
      prev.map((item) => {
        if (item.number != currentStepNumber)
          return { ...item, tabStatus: "nonactive" };
        return { ...item, tabStatus: "active" };
      }),
    );
  }, [currentStepNumber]);

  useEffect(() => {
    if (isMobile) setShowPanduan(false);
    else setShowPanduan(true);
  }, [isMobile]);

  useEffect(() => {
    if (!dataBerkas) return;

    const listNamaBerkas = dataBerkas?.listSysBerkas?.map((row: SysBerkas) => {
      return row.jenis_berkas?.nama_jenis_berkas;
    });

    setListPanduanPenerimaan((prev) =>
      prev?.map((value) => {
        if (value.title.startsWith("Berkas")) {
          return {
            ...value,
            contents: value.contents.map((content) => {
              if (content.title.startsWith("Berkas")) {
                return {
                  ...content,
                  instructions: listNamaBerkas,
                };
              }
              return content;
            }),
          };
        }
        return value;
      }),
    );
  }, [dataBerkas]);

  useEffect(() => {
    if (timePassed.waktu_pengumuman) {
      flushSync(() => {
        setSessionPeriode({
          id_sys_periode: dataPeriode?.id_sys_periode || "",
        });
      });
      navigate(`/pengumuman`);
    }
  }, [timePassed, navigate, sessionPeriode?.id_sys_periode]);

  if (isPendingPeriode) {
    return (
      <div className="flex flex-col gap-y-3">
        {Array(10)
          .fill(null)
          .map((_, index) => {
            return <SkeletonRow key={index} />;
          })}
      </div>
    );
  }

  const isDisableInput =
    isValidPendaftaran || timePassedActive?.waktu_selesai_daftar_valid_selesai;
  const isRegistrationEnded =
    timePassedActive?.waktu_selesai_daftar_valid_selesai;

  if (
    dataPeriode &&
    dataPeriode?.jalur_jenis?.nama_jalur_jenis_slug !== "internasional"
  ) {
    return <Navigate to="/dashboard" />;
  }

  return (
    <main>
      <SectionHeader title={`Admission - International Program`} />

      <Card
        className={`mb-6 border-l-4 shadow-sm ${
          isRegistrationEnded
            ? "border-l-amber-500 bg-amber-50/30 dark:bg-amber-950/10"
            : "border-l-blue-500 bg-blue-50/30 dark:bg-blue-950/10"
        }`}
      >
        <CardContent className="p-5">
          <div className="flex flex-col md:flex-row gap-6">
            {/* --- SECTION 1: REGISTRATION STATUS --- */}
            <div className="flex-1 flex gap-4">
              <div
                className={`p-2.5 rounded-full h-fit shrink-0 ${
                  isRegistrationEnded
                    ? "bg-amber-100 text-amber-600 dark:bg-amber-900 dark:text-amber-400"
                    : "bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-400"
                }`}
              >
                {isRegistrationEnded ? (
                  <AlertTriangle size={24} />
                ) : (
                  <Info size={24} />
                )}
              </div>
              <div className="space-y-2">
                <h3 className="font-bold text-lg text-slate-800 dark:text-slate-100">
                  {isRegistrationEnded
                    ? "Registration Period Ended"
                    : "Registration Information"}
                </h3>

                {isRegistrationEnded &&
                  dataPeriodeActive?.waktu_selesai_daftar_valid && (
                    <div className="flex items-center gap-2 text-sm font-medium text-amber-700 dark:text-amber-400 bg-amber-100/50 dark:bg-amber-900/20 px-3 py-1.5 rounded-md w-fit">
                      <CalendarClock size={16} />
                      <span>
                        Ended on{" "}
                        {formatDate(
                          dataPeriodeActive.waktu_selesai_daftar_valid,
                          undefined,
                          "en-US",
                        )}
                      </span>
                    </div>
                  )}

                <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                  Registration is considered complete only after you have{" "}
                  <span
                    className={`font-bold underline decoration-2 underline-offset-2 ${
                      isRegistrationEnded
                        ? "decoration-amber-500 text-amber-700"
                        : "decoration-blue-500 text-blue-700"
                    }`}
                  >
                    validated your data
                  </span>{" "}
                  and received a Participant Card.
                </p>
              </div>
            </div>

            {/* Separator untuk Mobile/Desktop */}
            <div className="hidden md:block w-[1px] bg-slate-200 dark:bg-slate-700" />
            <Separator className="md:hidden" />

            {/* --- SECTION 2: ANNOUNCEMENT --- */}
            <div className="md:w-1/3 flex gap-4">
              <div className="p-2.5 rounded-full bg-slate-100 text-slate-600 h-fit shrink-0 dark:bg-slate-800 dark:text-slate-400">
                <Megaphone size={24} />
              </div>
              <div className="space-y-1">
                <h4 className="font-semibold text-slate-800 dark:text-slate-200">
                  Announcement
                </h4>
                <div className="text-sm text-slate-600 dark:text-slate-400 space-y-2">
                  <p className="leading-snug">
                    Tuition fee (UK) determination is currently in process.
                  </p>

                  {/* Badge Status */}
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300">
                    <CheckCircle2 size={12} />
                    <span>Coming Soon</span>
                  </div>

                  {/* Uncomment this when dates are ready */}
                  {/* {dataPeriode?.waktu_pengumuman && (
                <p className="pt-1 border-t border-dashed border-slate-300 mt-2">
                  Result:{" "}
                  <span className="font-semibold text-primary">
                    {formatDate(dataPeriode.waktu_pengumuman, undefined, "en-US")}
                  </span>
                </p>
              )} */}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      <div className="flex lg:flex-col flex-row lg:gap-10">
        <Stepsbar
          steps={steps}
          goToStep={goToStep}
          isPending={isPendingSteps}
        />
        <div className="flex-1 h-screen overflow-auto">
          <div className="flex lg:flex-row flex-col lg:gap-5 gap-3">
            {currentStepNumber !== 2 && (
              <Card className={`${showPanduan ? "lg:w-[20rem]" : ""} h-fit`}>
                <div className="flex justify-between">
                  {showPanduan && (
                    <h1 className="mb-5 font-bold text-xl">Instructions</h1>
                  )}
                  <div className="flex items-center flex-col gap-y-3">
                    {!showPanduan && (
                      <span className="text-sm font-bold">Instructions</span>
                    )}
                    <Button
                      variant={"outline"}
                      size={"icon"}
                      onClick={() => setShowPanduan((show) => !show)}
                    >
                      <LuChevronRight
                        className={`${
                          showPanduan ? "rotate-90" : "rotate-0"
                        } duration-300`}
                      />
                    </Button>
                  </div>
                </div>
                <section className="flex flex-col gap-y-3">
                  {showPanduan &&
                    listPanduanPenerimaan.map((panduan, index) => {
                      return (
                        <div key={index}>
                          <Accordion type="single" collapsible>
                            <AccordionItem value="item-1">
                              <AccordionTrigger className="text-start">
                                {panduan.title}
                              </AccordionTrigger>
                              <AccordionContent>
                                <div className="flex flex-col gap-1">
                                  {panduan.contents.map((content) => {
                                    return (
                                      <div key={content.title}>
                                        <div className="flex items-center gap-1">
                                          <span>-</span>
                                          <h1 className="font-semibold">
                                            {content.title}
                                          </h1>
                                        </div>
                                        <ul>
                                          {content.instructions.map(
                                            (instruction, indexInstruction) => {
                                              return (
                                                <li
                                                  key={indexInstruction}
                                                  className="text-muted-foreground"
                                                >
                                                  <span>
                                                    {indexInstruction + 1}.{" "}
                                                  </span>
                                                  {instruction}
                                                </li>
                                              );
                                            },
                                          )}
                                        </ul>
                                      </div>
                                    );
                                  })}
                                </div>
                              </AccordionContent>
                            </AccordionItem>
                          </Accordion>
                        </div>
                      );
                    })}
                </section>
              </Card>
            )}

            <div className="flex-1 overflow-auto">
              {currentStep}

              {/* Wrapper Navigation */}
              <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-800 flex flex-col-reverse sm:flex-row justify-between items-center gap-4">
                {/* Tombol KIRI (Kembali) */}
                <div className="w-full sm:w-auto">
                  {!firstStep && (
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={backStep}
                      className="w-full sm:w-auto text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100 transition-colors"
                    >
                      <ChevronLeft className="w-4 h-4 mr-2" />
                      Back
                    </Button>
                  )}
                </div>

                {/* Tombol KANAN (Group Aksi) */}
                <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                  {/* Tombol Simpan (Hanya di step awal & bisa diedit) */}
                  {firstStep && !isDisableInput && (
                    <Button
                      type="button"
                      variant="secondary" // Menggunakan varian secondary agar tidak rebutan fokus dengan 'Berikutnya'
                      className="w-full sm:w-auto bg-emerald-50 text-emerald-600 hover:bg-emerald-100 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800"
                      disabled={isPendingSaveBiodata}
                      onClick={handleSaveBiodata}
                    >
                      {isPendingSaveBiodata ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Save
                        </>
                      )}
                    </Button>
                  )}

                  {/* Tombol Berikutnya */}
                  {!lastStep && (
                    <Button
                      type="button"
                      variant="default" // Primary action
                      className="w-full sm:w-auto"
                      disabled={currentIndex === currentIndexDisable - 1} // Gunakan properti disabled asli
                      onClick={handleNextStep}
                    >
                      Next
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
